import cuid from "cuid";
import {
  defaultTo,
  defaultsDeep,
  filter,
  get,
  head,
  isBoolean,
  isEmpty,
  isNil,
  isNumber,
  isObject,
  keyBy,
  map,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import {
  createFieldStore,
  FieldRegistryProvider,
} from "shared/hooks/useFieldRegistry";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { USER_AUTH } from "utils/api/graphql/queries/users";
import { normalizeAcorisProfile } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/insurancePlan";
import { resolveDefaultAddressCountry } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/zipCode";
import { withDocumentsCompletion } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/documentsCompletion";
import { resolveIsPep } from "./Widgets/AdditionalInformation/ppeDeclaration";
import {
  ACORIS_ACTIVITY_STATUS,
  ACORIS_PROFILES,
  ATTACHMENT_TYPES,
  BANK_ACCOUNT_OWNER_TYPE,
  CASE_TYPES,
  CONTACT_POINT_SYSTEM,
  PAYMENT_RECEIVER,
  PROJECT_SOURCE,
  PROJECT_TYPES,
  RELATIONSHIP,
  SALE_MODES,
  SUBSCRIPTION_FORM_KEYS,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import {
  getAgeFromBirthDate,
  normalizeAcorisChildRelatedPerson,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/relatedPersonChild";
import {
  resolveAcorisContractMoreThanOneYear,
  resolveAcorisTerminationRequest,
} from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/terminationRequest";
import { isAcorisNonResponsiblePlan } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/insurancePlan";
import { resolveIdentityDocumentSides } from "screens/Private/Broker/Projects/Detail/Templates/Comparison/Acoris/utils/identityDocuments";
import View from "./View";
import { DOCUMENTS_TYPE } from "./Widgets/Proof";

// Prospect profiles that trigger the conditional "Justificatif d'éligibilité"
// (JSON parcours: onglet-2 — profils soumis à éligibilité).
// TODO(acoris): refine per-profile eligibility rules once the classeur
// 7_Pieces_justif covers every profile.
const ELIGIBILITY_PROFILES = [
  ACORIS_PROFILES.TERRITORIAL_AGENT,
  ACORIS_PROFILES.TNS,
  ACORIS_PROFILES.CROSS_BORDER_WORKER,
];

const CHILD_ID_AGE_THRESHOLD = 18;

const buildChildProofKeys = (children, prefix) =>
  children.map((child, index) => {
    const userId = get(child, "identity.user.id");
    return userId ? `${prefix}_${userId}` : `${prefix}_${index}`;
  });

/**
 * ACORIS proof matrix (§7.2 — participant × piece × condition). Shared by the
 * initial computation and the payment-condition recompute effect so both stay
 * in sync. Facultative pieces (justificatif de domicile) stay in the UI with
 * an empty progress list so they never block signature.
 */
const buildProofFields = ({
  hasSpouse,
  children,
  requiresThirdPartyPayer,
  requiresTwoSeparateRibs,
  requiresEligibilityProof,
}) => {
  const adultChildren = children.filter(
    (child) =>
      getAgeFromBirthDate(get(child, "identity.user.birthDate")) >=
      CHILD_ID_AGE_THRESHOLD,
  );

  return {
    // Attestation de droit au régime — obligatoire (adhérent + conjoint + enfants).
    [SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE]: [
      "proof.socialSecurityCertificate",
      ...(hasSpouse ? ["proof.spouseSocialSecurityCertificate"] : []),
      ...buildChildProofKeys(children, "proof.childSocialSecurityCertificate"),
    ],
    // Pièces d'identité : adhérent, conjoint, tiers payeur, enfants majeurs.
    [SUBSCRIPTION_FORM_KEYS.PROOF.ID]: [
      "proof.cinRecto",
      "proof.cinVerso",
      ...(hasSpouse ? ["proof.spouseCinRecto", "proof.spouseCinVerso"] : []),
      ...(requiresThirdPartyPayer
        ? ["proof.payerCinRecto", "proof.payerCinVerso"]
        : []),
      ...buildChildProofKeys(adultChildren, "proof.childCinRecto"),
      ...buildChildProofKeys(adultChildren, "proof.childCinVerso"),
    ],
    // RIB cotisations (+ RIB Prestation si autres comptes).
    [SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: requiresTwoSeparateRibs
      ? ["proof.paymentAccount", "proof.refundAccount"]
      : ["proof.paymentAccount"],
    // Justificatif de domicile — facultatif (hors progress).
    [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ADDRESS]: [],
    // Attestation sur l'honneur du tiers payeur.
    ...(requiresThirdPartyPayer && {
      [SUBSCRIPTION_FORM_KEYS.PROOF.CONTRIBUTION_PAYMENT_AGREEMENT]: [
        "proof.contributionPaymentAgreement",
      ],
    }),
    // Justificatif d'éligibilité (profils soumis à éligibilité).
    ...(requiresEligibilityProof && {
      [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ELIGIBILITY]: [
        "proof.proofOfEligibility",
      ],
    }),
    // Certificat de scolarité (enfants ≥ 18 ans).
    ...(adultChildren.length && {
      [SUBSCRIPTION_FORM_KEYS.PROOF.STUDENT_CERTIFICATE]: buildChildProofKeys(
        adultChildren,
        "proof.childStudentCertificate",
      ),
    }),
  };
};

const CONSENT_PROGRESS_FIELD_PATTERN = /^project\.fields\.accept/;

/** Whether a registry field counts as filled for block progress. */
const isProgressFieldFilled = (fieldPath, value) => {
  if (isBoolean(value)) {
    if (CONSENT_PROGRESS_FIELD_PATTERN.test(fieldPath)) {
      return value === true;
    }
    return !isNil(value);
  }
  if (isNil(value) || value === "") {
    return false;
  }
  if (isObject(value) && !isEmpty(value)) {
    return true;
  }
  if (isObject(value)) {
    return false;
  }
  return true;
};

/** RIA flow: only count fields visible for the current termination path. */
const buildTerminationRequestProgressFields = (project = {}) => {
  const contractMoreThanOneYear = get(
    project,
    "fields.contractMoreThanOneYear",
  );
  const selfOccupation = get(project, "fields.selfOccupation");
  const fields = ["project.fields.contractMoreThanOneYear"];

  if (contractMoreThanOneYear === true) {
    fields.push("project.fields.selfOccupation");
    if (selfOccupation === false) {
      fields.push(
        "project.terminationRequest.contractNumber",
        "project.terminationRequest.zipCode",
        "project.terminationRequest.city",
        "project.terminationRequest.address",
        "project.terminationRequest.brokerageMandate.currentCompany",
        "project.terminationRequest.dueDate",
      );
    }
  }

  return fields;
};

const buildActivityProgressFields = (contact = {}, insurancePlan = {}) => {
  const fields = [
    "project.contact.fields.status",
    "project.contact.profession",
    "project.contact.familySituation",
  ];
  const status = get(contact, "fields.status");

  if (
    get(insurancePlan, "fields.madelin") &&
    status === ACORIS_ACTIVITY_STATUS.INDEPENDANT
  ) {
    fields.push("project.contact.optMadelin");
  }

  return fields;
};

const buildRefundProgressFields = (isPayerReceiver) => {
  const fields = ["isPayerReceiver"];

  if (isPayerReceiver === PAYMENT_RECEIVER.ANOTHER_ACCOUNT) {
    fields.push(
      "payment.receiver.owner.firstName",
      "payment.receiver.owner.lastName",
      "payment.receiver.IBAN",
      "payment.receiver.BIC",
    );
  }

  return fields;
};

const resolveAdditionalInformationBlockFields = (
  block,
  values,
  staticFields,
) => {
  switch (block) {
    case SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST:
      return buildTerminationRequestProgressFields(get(values, "project"));
    case SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY:
      return buildActivityProgressFields(
        get(values, "project.contact"),
        get(values, "insurancePlan"),
      );
    case SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND:
      return buildRefundProgressFields(get(values, "isPayerReceiver"));
    case SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PPE:
      return [
        "project.contact.politicallyExposedPerson",
        ...(get(values, "project.contact.politicallyExposedPerson") === true
          ? [
              "project.contact.fields.ppeFunction",
              "project.contact.fields.ppeStartDate",
            ]
          : []),
      ];
    default:
      return staticFields;
  }
};

export const STEPPER_KEYS = {
  ADDITIONAL_INFORMATION: "ADDITIONAL_INFORMATION",
  PROOF: "PROOF",
  DOCUMENT: "DOCUMENT",
};

const Detail = () => {
  const { id } = useParams();
  const fieldStore = useMemo(() => createFieldStore(), []);
  const [initialValues, setInitialValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkUser] = useLazyQuery(USER_AUTH);
  const [updateSubscription] = useMutation(
    graphql.mutations.UPDATE_SUBSCRIPTION,
    {
      refetchQueries: [
        {
          query: graphql.queries.SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id } },
        },
      ],
    },
  );

  const initSubscription = useCallback(({ subscription: data }) => {
    const subscription = omitDeep(data, "__typename");
    const subscriptionPayment = get(subscription, "payment");
    const paymentsTypes = get(
      subscription,
      "insurancePlan.fields.payment.type",
      [],
    );
    const debitDays = get(
      subscription,
      "insurancePlan.fields.payment.debitDay",
      [],
    );
    const frequencies = get(
      subscription,
      "insurancePlan.fields.payment.frequency",
      [],
    );
    const shouldBeAdherent =
      get(subscription, "payment.payer.owner.isAdherent", true) ||
      get(subscription, "insurancePlan.fields.payment.shouldBeAdherent", false);
    if (isEmpty(subscription)) return;
    const contactTelecoms = get(subscription, "project.contact.telecoms", []);
    const contactZipCode = get(
      (contactTelecoms || []).find(
        (telecom) => telecom.system === CONTACT_POINT_SYSTEM.ZIP_CODE,
      ),
      "value",
    );
    const payerTelecoms = get(subscription, "payment.payer.owner.telecoms", []);
    const payerZipCode = get(
      (shouldBeAdherent ? contactTelecoms : payerTelecoms || []).find(
        (telecom) => telecom.system === CONTACT_POINT_SYSTEM.ZIP_CODE,
      ),
      "value",
    );
    const receiverTelecoms = get(
      subscription,
      "payment.receiver.owner.telecoms",
      [],
    );
    // Check if payer and receiver are the same account
    const isSameAccount =
      get(subscription, "payment.payer.id", "") ===
      get(subscription, "payment.receiver.id");
    const selfOccupationDisabled =
      get(subscription, "project.fields.selfOccupation") !== false;
    const profileType = normalizeAcorisProfile(
      get(subscription, "project.contact.fields.profile") ||
        get(subscription, "project.fields.profileType"),
    );
    const requiresEligibilityProof = ELIGIBILITY_PROFILES.includes(profileType);

    const initialDisabled = {
      project: {
        ria: true,
        contract: {
          caseType: get(subscription, "project.ria", false),
          saleMode:
            get(subscription, "project.fields.twoTimesSale", false) ||
            get(subscription, "project.fields.source") ===
              PROJECT_SOURCE.CLIENT,
        },
        contact: {
          siretNumber: true,
          optMadelin: !get(subscription, "insurancePlan.fields.madelin"),
          fields: {
            affiliateOrganizationCode: true,
            profile: true,
          },
          profession: get(
            subscription,
            "insurancePlan.config.disabled",
            [],
          ).includes("project.contact.profession"),
        },
        projectType:
          get(subscription, "project.fields.source") ===
            PROJECT_SOURCE.CLIENT ||
          get(subscription, "project.fields.twoTimesSale") ||
          [SALE_MODES.FACE_TO_FACE].includes(
            get(subscription, "project.contract.saleMode"),
          ),
        terminationRequest: {
          dueDate: selfOccupationDisabled,
          contractNumber: selfOccupationDisabled,
          zipCode: selfOccupationDisabled,
          city: selfOccupationDisabled,
          address: selfOccupationDisabled,
          brokerageMandate: {
            currentCompany: selfOccupationDisabled,
          },
        },
      },
      createdDate: true,
      proof: {
        // Adherent ID is always required for ACORIS (JSON parcours: onglet-2).
        cinRecto: false,
        cinVerso: false,
        // payerCin disabled when payer is adherent (no third-party payer)
        payerCinRecto: get(
          subscription,
          "payment.payer.owner.isAdherent",
          true,
        ),
        payerCinVerso: get(
          subscription,
          "payment.payer.owner.isAdherent",
          true,
        ),
        // refundAccount disabled when payer and receiver are the same account
        refundAccount: isSameAccount,
      },
      // Disable receiver payment fields when same account (values come from payer)
      payment: {
        receiver: {
          owner: {
            firstName: isSameAccount,
            lastName: isSameAccount,
          },
          IBAN: isSameAccount,
          BIC: isSameAccount,
        },
        payer: {
          owner: {
            firstName: shouldBeAdherent,
            lastName: shouldBeAdherent,
          },
        },
      },
      telecoms: {
        payer: {
          [CONTACT_POINT_SYSTEM.ADDRESS]: {
            value: shouldBeAdherent,
          },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: {
            value: shouldBeAdherent,
          },
          [CONTACT_POINT_SYSTEM.CITY]: {
            value: shouldBeAdherent,
          },
          [CONTACT_POINT_SYSTEM.COUNTRY]: {
            value: shouldBeAdherent,
          },
        },
        // Disable receiver telecoms when same account (values come from payer)
        receiver: {
          [CONTACT_POINT_SYSTEM.ADDRESS]: {
            value: isSameAccount,
          },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: {
            value: isSameAccount,
          },
          [CONTACT_POINT_SYSTEM.CITY]: {
            value: isSameAccount,
          },
        },
        contact: {
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: {
            value: true,
          },
        },
      },
      [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]: !get(
        subscription,
        "project.ria",
        false,
      ),
      // [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: !get(
      //   subscription,
      //   "project.ria",
      //   false
      // ),
      [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: !get(
        subscription,
        "project.contact.relatedPersons",
        [],
      ).find((person) => person.relationship === RELATIONSHIP.SPOUSE),
      [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]: !get(
        subscription,
        "project.contact.relatedPersons",
        [],
      ).find((person) => person.relationship === RELATIONSHIP.CHILD),
      // Justificatif d'éligibilité — only for eligibility-driven profiles.
      [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ELIGIBILITY]:
        !requiresEligibilityProof,
      // Attestation sur l'honneur du tiers payeur — only when payeur ≠ adhérent.
      [SUBSCRIPTION_FORM_KEYS.PROOF.CONTRIBUTION_PAYMENT_AGREEMENT]: get(
        subscription,
        "payment.payer.owner.isAdherent",
        true,
      ),
      // Justificatif de domicile — facultatif (affiché, hors progress).
      [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ADDRESS]: false,
    };

    const telecoms = defaultsDeep(
      {
        contact: keyBy(contactTelecoms, "system"),
        payer: keyBy(
          shouldBeAdherent ? contactTelecoms : payerTelecoms,
          "system",
        ),
        receiver: keyBy(receiverTelecoms, "system"),
      },
      {
        contact: {
          [CONTACT_POINT_SYSTEM.ADDRESS]: { value: null },
          [CONTACT_POINT_SYSTEM.ADDRESS_LOCATION]: { value: null },
          [CONTACT_POINT_SYSTEM.COMPLEMENT]: { value: null },
          [CONTACT_POINT_SYSTEM.RESIDENCE]: { value: null },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
          [CONTACT_POINT_SYSTEM.CITY]: { value: null },
          [CONTACT_POINT_SYSTEM.COUNTRY]: {
            value: resolveDefaultAddressCountry(contactZipCode),
          },
          [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: { value: null },
          [CONTACT_POINT_SYSTEM.BIRTH_CITY]: { value: null },
          [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
          [CONTACT_POINT_SYSTEM.LANDLINE]: { value: null },
          [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
        },
        payer: {
          [CONTACT_POINT_SYSTEM.ADDRESS]: { value: null },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
          [CONTACT_POINT_SYSTEM.CITY]: { value: null },
          [CONTACT_POINT_SYSTEM.COUNTRY]: {
            value: resolveDefaultAddressCountry(payerZipCode),
          },
        },
      },
    );
    const children = filter(
      get(subscription, "project.contact.relatedPersons", []),
      {
        relationship: RELATIONSHIP.CHILD,
      },
    ).map((child) =>
      defaultsDeep(normalizeAcorisChildRelatedPerson(child), {
        identity: { fields: { teletransmission: true } },
      }),
    );

    // Determine payment conditions for document requirements
    const payment = get(subscription, "payment");
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", true);
    const requiresThirdPartyPayer = !payerIsAdherent;
    const requiresTwoSeparateRibs = !isPayerReceiver;

    // Check for spouse
    const hasSpouse = !isEmpty(
      get(subscription, "project.contact.relatedPersons", []).find(
        (person) => person?.relationship === RELATIONSHIP.SPOUSE,
      ),
    );

    const fields = {
      [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTRACT]: [
          "project.contract.issuanceDate",
          "project.contract.caseType",
          // "project.ria",
          "project.contract.saleMode",
          "project.fields.commercializationType",
          "project.projectType",
          "createdDate",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]: [
          "project.fields.contractMoreThanOneYear",
          "project.fields.selfOccupation",
          "project.terminationRequest.contractNumber",
          "project.terminationRequest.zipCode",
          "project.terminationRequest.city",
          "project.terminationRequest.address",
          "project.terminationRequest.brokerageMandate.currentCompany",
          "project.terminationRequest.dueDate",
        ],
        // [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: [
        // "project.terminationRequest.brokerageMandate.lastName",
        // "project.terminationRequest.brokerageMandate.firstName",
        // "project.terminationRequest.brokerageMandate.currentCompany",
        // ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT]: [
          "project.contact.user.lastname",
          "project.contact.user.firstname",
          "project.contact.user.birthDate",
          "project.contact.socialSecurityNumber",
          "project.contact.nationality",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY]: [
          "project.contact.fields.status",
          "project.contact.profession",
          "project.contact.familySituation",
          "project.contact.optMadelin",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT]: [
          `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: [
          "spouse.identity.user.firstname",
          "spouse.identity.user.lastname",
          "spouse.identity.user.birthDate",
          "spouse.identity.socialSecurityNumber",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]:
          children.flatMap((_, i) => [
            `children.${i}.identity.user.firstname`,
            `children.${i}.identity.user.lastname`,
            `children.${i}.identity.user.birthDate`,
            `children.${i}.identity.socialSecurityNumber`,
          ]),
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT]: [
          "payment.type",
          "payment.frequency",
          "payment.debitDay",
          "payment.payer.IBAN",
          "payment.payer.BIC",
          "payment.payer.owner.isAdherent",
          "payment.payer.owner.firstName",
          "payment.payer.owner.lastName",
          `telecoms.payer.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
          `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
          `telecoms.payer.${CONTACT_POINT_SYSTEM.COUNTRY}.value`,
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND]: [
          "isPayerReceiver",
          "payment.receiver.owner.firstName",
          "payment.receiver.owner.lastName",
          "payment.receiver.IBAN",
          "payment.receiver.BIC",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION]: [
          "project.fields.acceptDocumentsReceived",
          "project.fields.acceptAgreement",
          "project.fields.acceptFees",
          ...(isAcorisNonResponsiblePlan(subscription.insurancePlan)
            ? ["project.fields.acceptProductStatuses"]
            : []),
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PPE]: [
          "project.contact.politicallyExposedPerson",
          ...(get(subscription, "project.contact.politicallyExposedPerson") ===
          true
            ? [
                "project.contact.fields.ppeFunction",
                "project.contact.fields.ppeStartDate",
              ]
            : []),
        ],
      },
      [STEPPER_KEYS.PROOF]: buildProofFields({
        hasSpouse,
        children,
        requiresThirdPartyPayer,
        requiresTwoSeparateRibs,
        requiresEligibilityProof,
      }),
    };

    const spouse = get(subscription, "project.contact.relatedPersons", []).find(
      ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
    );

    const resolvedTerminationRequest = resolveAcorisTerminationRequest(
      subscription.project,
    );
    const resolvedContractMoreThanOneYear =
      resolveAcorisContractMoreThanOneYear(subscription.project);
    const resolvedSelfOccupation = get(
      subscription,
      "project.fields.selfOccupation",
      resolvedTerminationRequest ? false : undefined,
    );

    const additionalInfoInitialValues = {
      ...subscription,
      telecoms,
      project: {
        ...subscription.project,
        ria: get(subscription, "project.ria", resolvedContractMoreThanOneYear),
        fields: {
          ...subscription.project.fields,
          contractMoreThanOneYear: resolvedContractMoreThanOneYear,
          selfOccupation: resolvedSelfOccupation,
          acceptAgreement:
            get(subscription, "project.fields.acceptAgreement") ??
            get(subscription, "project.fields.acceptDataProcessing"),
          acceptFees:
            get(subscription, "project.fields.acceptFees") ??
            get(subscription, "project.fields.acceptDigitalCommunication"),
          acceptProductStatuses:
            get(subscription, "project.fields.acceptProductStatuses") ??
            get(subscription, "project.fields.acceptNonResponsible"),
          refundSameAsPayment: get(
            subscription,
            "project.fields.refundSameAsPayment",
            isSameAccount,
          ),
        },
        ...(subscription.project.ria || resolvedContractMoreThanOneYear
          ? {
              terminationRequest: {
                id: cuid(),
                ...resolvedTerminationRequest,
                brokerageMandate: {
                  id: cuid(),
                  firstName: get(
                    subscription,
                    "project.contact.user.firstname",
                  ),
                  lastName: get(subscription, "project.contact.user.lastname"),
                  ...resolvedTerminationRequest?.brokerageMandate,
                },
              },
            }
          : {}),
        ...(subscription.project.fields.twoTimesSale && {
          projectType: PROJECT_TYPES.COLD_LEAD,
        }),
        contact: {
          ...defaultsDeep(subscription.project.contact, {
            fields: { teletransmission: true, familySituation: null },
            acceptDigital:
              get(subscription, "project.contact.acceptDigital") ??
              get(subscription, "project.fields.acceptProspection"),
            acceptContact:
              get(subscription, "project.contact.acceptContact") ??
              get(subscription, "project.fields.acceptPhoneMarketing"),
          }),
          politicallyExposedPerson: resolveIsPep(
            get(subscription, "project.contact.politicallyExposedPerson"),
          ),
        },
        contract: {
          ...subscription.project.contract,
          ...(subscription.project.ria || resolvedContractMoreThanOneYear
            ? {
                caseType: CASE_TYPES.RETURN_TO_COMPETITION,
              }
            : {}),
          ...(subscription.project.fields.twoTimesSale && {
            saleMode: SALE_MODES.DISTANT,
          }),
        },
      },
      isPayerReceiver:
        get(subscription, "payment.payer.id", "") ===
        get(subscription, "payment.receiver.id")
          ? PAYMENT_RECEIVER.SAME_ACCOUNT
          : PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
      payment: {
        id: cuid(),
        ...subscriptionPayment,
        type: get(
          subscriptionPayment,
          "type",
          paymentsTypes.length === 1 ? head(paymentsTypes) : null,
        ),
        debitDay: get(
          subscriptionPayment,
          "debitDay",
          debitDays.length === 1 ? head(debitDays) : null,
        ),
        frequency: get(
          subscriptionPayment,
          "frequency",
          frequencies.length === 1 ? head(frequencies) : null,
        ),
        payer: {
          id: cuid(),
          IBAN: null,
          BIC: null,
          ...subscription.payment?.payer,
          owner: {
            id: cuid(),
            ...subscription.payment?.payer?.owner,
            firstName: defaultTo(
              subscription.payment?.payer?.owner?.firstName,
              shouldBeAdherent
                ? get(subscription, "project.contact.user.firstname")
                : null,
            ),
            lastName: defaultTo(
              subscription.payment?.payer?.owner?.lastName,
              shouldBeAdherent
                ? get(subscription, "project.contact.user.lastname")
                : null,
            ),
            isAdherent: defaultTo(
              subscription.payment?.payer?.owner?.isAdherent,
              shouldBeAdherent,
            ),
            type: get(subscription, "insurancePlan.fields.tns")
              ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
              : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
          },
        },
        receiver: defaultsDeep(subscription.payment?.receiver, {
          id: cuid(),
          IBAN: null,
          BIC: null,
          owner: { id: cuid() },
        }),
      },
      children,
      spouse: isEmpty(spouse)
        ? undefined
        : defaultsDeep(spouse, {
            identity: { fields: { teletransmission: true } },
          }),
    };

    // Build proof initial values from attachments
    const attachments = get(subscription, "attachments", []);

    // Get user IDs for filtering
    const adherentUserId = get(subscription, "project.contact.user.id");
    const spouseData = get(
      subscription,
      "project.contact.relatedPersons",
      [],
    ).find((person) => person?.relationship === RELATIONSHIP.SPOUSE);
    const spouseUserId = get(spouseData, "identity.user.id");
    const payerUserId = get(subscription, "payment.payer.owner.user.id");
    const receiverUserId = get(subscription, "payment.receiver.owner.user.id");

    // Helper to filter attachments by type and user
    const filterAttachmentsByTypeAndUser = (type, userId) =>
      map(
        filter(attachments, (att) => {
          const attUserId = get(att, "user.id") || get(att, "userId");
          return att.type === type && attUserId === userId;
        }),
        ({ id, name, fileUrl }) => ({
          uid: id,
          name,
          status: "done",
          url: fileUrl,
        }),
      );

    const filterAttachmentsByUser = (userId) =>
      attachments.filter((att) => {
        const attUserId = get(att, "user.id") || get(att, "userId");
        return attUserId === userId;
      });

    const resolveUserIdentitySides = (userId, types) =>
      resolveIdentityDocumentSides(filterAttachmentsByUser(userId), types);

    const adherentIdentity = resolveUserIdentitySides(adherentUserId, {
      rectoType: ATTACHMENT_TYPES.CIN_RECTO,
      versoType: ATTACHMENT_TYPES.CIN_VERSO,
      legacyType: ATTACHMENT_TYPES.CIN,
    });

    const payerIdentity = resolveUserIdentitySides(
      payerUserId || adherentUserId,
      {
        rectoType: ATTACHMENT_TYPES.PAYER_CIN_RECTO,
        versoType: ATTACHMENT_TYPES.PAYER_CIN_VERSO,
        legacyType: ATTACHMENT_TYPES.PAYER_CIN,
      },
    );

    const spouseIdentity = spouseUserId
      ? resolveUserIdentitySides(spouseUserId, {
          rectoType: ATTACHMENT_TYPES.CIN_RECTO,
          versoType: ATTACHMENT_TYPES.CIN_VERSO,
          legacyType: ATTACHMENT_TYPES.CIN,
        })
      : { recto: [], verso: [] };

    // Map each document type to the correct user (JSON parcours: onglet-2).
    const proofDocuments = {
      cinRecto: adherentIdentity.recto,
      cinVerso: adherentIdentity.verso,
      socialSecurityCertificate: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.socialSecurityCertificate,
        adherentUserId,
      ),
      proofOfAddress: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.proofOfAddress,
        adherentUserId,
      ),
      proofOfEligibility: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.proofOfEligibility,
        adherentUserId,
      ),
      paymentAccount: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.paymentAccount,
        !payerIsAdherent && payerUserId ? payerUserId : adherentUserId,
      ),
      payerCinRecto: payerIdentity.recto,
      payerCinVerso: payerIdentity.verso,
      contributionPaymentAgreement: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.contributionPaymentAgreement,
        payerUserId || adherentUserId,
      ),
      refundAccount: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.refundAccount,
        receiverUserId || adherentUserId,
      ),
      spouseCinRecto: spouseIdentity.recto,
      spouseCinVerso: spouseIdentity.verso,
      spouseSocialSecurityCertificate: spouseUserId
        ? filterAttachmentsByTypeAndUser(
            DOCUMENTS_TYPE.spouseSocialSecurityCertificate,
            spouseUserId,
          )
        : [],
    };

    // Add per-child documents dynamically (attestation régime, CIN majeur,
    // certificat de scolarité).
    children.forEach((child, index) => {
      const userId = get(child, "identity.user.id");
      const suffix = userId || index;
      const childIdentity = resolveUserIdentitySides(userId, {
        rectoType: ATTACHMENT_TYPES.CIN_RECTO,
        versoType: ATTACHMENT_TYPES.CIN_VERSO,
        legacyType: ATTACHMENT_TYPES.CIN,
      });

      proofDocuments[`childSocialSecurityCertificate_${suffix}`] =
        filterAttachmentsByTypeAndUser(
          ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
          userId,
        );
      proofDocuments[`childCinRecto_${suffix}`] = childIdentity.recto;
      proofDocuments[`childCinVerso_${suffix}`] = childIdentity.verso;
      proofDocuments[`childStudentCertificate_${suffix}`] =
        filterAttachmentsByTypeAndUser(
          ATTACHMENT_TYPES.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE,
          userId,
        );
    });

    const proofInitialValues = {
      proof: proofDocuments,
    };
    setDisabled(initialDisabled);
    setInitialValues({
      [STEPPER_KEYS.ADDITIONAL_INFORMATION]: additionalInfoInitialValues,
      [STEPPER_KEYS.PROOF]: proofInitialValues,
      fields,
    });

    if (subscription.status === SUBSCRIPTION_STATUSES.SIGNED)
      setActive(STEPPER_KEYS.DOCUMENT);
    if (subscription.status === SUBSCRIPTION_STATUSES.PENDING)
      setActive(STEPPER_KEYS.DOCUMENT);
  }, []);

  const { data, loading, error } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id } },
    onCompleted: initSubscription,
  });

  const [active, setActive] = useState(STEPPER_KEYS.ADDITIONAL_INFORMATION);
  const [disabled, setDisabled] = useState({});
  const [realProgress, setRealProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: 0,
    [STEPPER_KEYS.PROOF]: 0,
  });
  const [progress, setProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {},
    [STEPPER_KEYS.PROOF]: {},
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [active]);

  // Update disabled state and fields when payment conditions change
  useEffect(() => {
    if (!data?.subscription) return;

    const subscription = data.subscription;

    // Recalculate payment conditions
    const payment = get(subscription, "payment");
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", true);
    const requiresThirdPartyPayer = !payerIsAdherent;
    const requiresTwoSeparateRibs = !isPayerReceiver;

    // Update disabled state for proof fields
    setDisabled((prev) => ({
      ...prev,
      proof: {
        ...prev.proof,
        payerCinRecto: payerIsAdherent,
        payerCinVerso: payerIsAdherent,
        refundAccount: isPayerReceiver,
      },
    }));

    // Update fields configuration
    setInitialValues((prev) => {
      if (!prev?.fields) return prev;

      const children = filter(
        get(subscription, "project.contact.relatedPersons", []),
        { relationship: RELATIONSHIP.CHILD },
      );
      const hasSpouse = !isEmpty(
        get(subscription, "project.contact.relatedPersons", []).find(
          (person) => person?.relationship === RELATIONSHIP.SPOUSE,
        ),
      );
      const profileType = normalizeAcorisProfile(
        get(subscription, "project.contact.fields.profile") ||
          get(subscription, "project.fields.profileType"),
      );
      const requiresEligibilityProof =
        ELIGIBILITY_PROFILES.includes(profileType);

      const newProofFields = buildProofFields({
        hasSpouse,
        children,
        requiresThirdPartyPayer,
        requiresTwoSeparateRibs,
        requiresEligibilityProof,
      });

      return {
        ...prev,
        fields: {
          ...prev.fields,
          [STEPPER_KEYS.PROOF]: newProofFields,
        },
      };
    });
  }, [data?.subscription]);

  // Recalculate progress when disabled state or initialValues.fields change
  useEffect(() => {
    if (!initialValues?.fields?.[STEPPER_KEYS.PROOF]) return;

    // Get current proof values from the Proof component's state
    const proofValues = initialValues[STEPPER_KEYS.PROOF];
    if (proofValues) {
      // Recalculate progress for all proof blocks
      Object.keys(initialValues.fields[STEPPER_KEYS.PROOF]).forEach((block) => {
        const blockProgress = (() => {
          if (
            disabled[block] &&
            block !== SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE
          )
            return undefined;

          const fields = initialValues.fields[STEPPER_KEYS.PROOF][block] || [];
          const maxLength = fields.filter((g) => g && !get(disabled, g)).length;
          const filledLength = fields.filter((g) => {
            const value = get(proofValues, g);
            return (
              !isNil(value) &&
              value !== "" &&
              (!isObject(value) || !isEmpty(value)) &&
              !get(disabled, g)
            );
          }).length;
          return maxLength ? Math.ceil((filledLength / maxLength) * 100) : 100;
        })();

        if (blockProgress !== undefined) {
          setProgress((prev) => ({
            ...prev,
            [STEPPER_KEYS.PROOF]: {
              ...prev[STEPPER_KEYS.PROOF],
              [block]: blockProgress,
            },
          }));
        }
      });

      // Recalculate mean progress
      const blocks = Object.keys(initialValues.fields[STEPPER_KEYS.PROOF]);
      const { count, sum } = blocks.reduce(
        (acc, block) => {
          if (
            disabled[block] &&
            block !== SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE
          )
            return acc;

          const fields = initialValues.fields[STEPPER_KEYS.PROOF][block] || [];
          const maxLength = fields.filter((g) => g && !get(disabled, g)).length;
          const filledLength = fields.filter((g) => {
            const value = get(proofValues, g);
            return (
              !isNil(value) &&
              value !== "" &&
              (!isObject(value) || !isEmpty(value)) &&
              !get(disabled, g)
            );
          }).length;
          const blockProgress = maxLength
            ? Math.ceil((filledLength / maxLength) * 100)
            : 100;

          return { sum: acc.sum + blockProgress, count: acc.count + 1 };
        },
        { count: 0, sum: 0 },
      );

      const meanProgress = count > 0 ? +(sum / count).toFixed(2) : 0;
      setRealProgress((prev) => ({
        ...prev,
        [STEPPER_KEYS.PROOF]: meanProgress,
      }));
    }
  }, [disabled, initialValues]);

  const calculateBlockProgress = (parent, block, values, disabled) => {
    if (
      disabled[block] &&
      block !== SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE
    )
      return;

    const staticFields = get(initialValues, `fields.${parent}.${block}`, []);
    const fields =
      parent === STEPPER_KEYS.ADDITIONAL_INFORMATION
        ? resolveAdditionalInformationBlockFields(block, values, staticFields)
        : staticFields;
    let fieldList = Array.isArray(fields) ? fields : Object.values(fields);

    // Align progress with mounted (applicable) fields so % and blocking
    // errors always agree. PROOF stays catalog-driven.
    if (parent === STEPPER_KEYS.ADDITIONAL_INFORMATION) {
      const registeredPaths = new Set(fieldStore.getRegisteredPaths());
      if (registeredPaths.size > 0) {
        fieldList = fieldList.filter((path) => registeredPaths.has(path));
      }
    }

    const maxLength = fieldList.filter((g) => g && !get(disabled, g)).length;
    const filedLength = fieldList.filter(
      (g) => g && !get(disabled, g) && isProgressFieldFilled(g, get(values, g)),
    ).length;

    return maxLength ? Math.ceil((filedLength / maxLength) * 100) : 100;
  };

  /** Mean progress for a step; avoids reading stale sibling state from realProgress. */
  const getProgressMean = (parent, stepValues, initialDisabled) => {
    const { count, sum } = Object.keys(
      get(initialValues, `fields.${parent}`, []),
    ).reduce(
      (acc, block) => {
        const blockProgress = calculateBlockProgress(
          parent,
          block,
          stepValues,
          initialDisabled,
        );
        return isNumber(blockProgress)
          ? { sum: (acc.sum += blockProgress), count: (acc.count += 1) }
          : acc;
      },
      { count: 0, sum: 0 },
    );
    return count > 0 ? +(sum / count).toFixed(2) : 0;
  };

  const calculateRealTimeProgress = (
    parent,
    block,
    values,
    initialDisabled = disabled,
  ) => {
    setProgress((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [block]: calculateBlockProgress(parent, block, values, initialDisabled),
      },
    }));
  };

  const calculateProgressMean = (
    parent,
    values,
    initialDisabled = disabled,
    options = {},
  ) => {
    const { skipPersistence = false } = options;
    const progress = getProgressMean(parent, values, initialDisabled);

    setRealProgress((prev) => ({
      ...prev,
      [parent]: progress,
    }));

    if (skipPersistence) {
      return;
    }

    if (
      parent === STEPPER_KEYS.PROOF &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      const additionalInfoMean = getProgressMean(
        STEPPER_KEYS.ADDITIONAL_INFORMATION,
        initialValues[STEPPER_KEYS.ADDITIONAL_INFORMATION],
        initialDisabled,
      );
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...withDocumentsCompletion(
                get(
                  omitDeep(data, "__typename"),
                  "subscription.additionalInfo",
                  {},
                ),
                progress === 100,
              ),
              isInfoCompleted: additionalInfoMean === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    if (
      parent === STEPPER_KEYS.ADDITIONAL_INFORMATION &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      const proofMean = getProgressMean(
        STEPPER_KEYS.PROOF,
        initialValues[STEPPER_KEYS.PROOF],
        initialDisabled,
      );
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...withDocumentsCompletion(
                get(
                  omitDeep(data, "__typename"),
                  "subscription.additionalInfo",
                  {},
                ),
                proofMean === 100,
              ),
              isInfoCompleted: progress === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    setIsSubmitting(false);
  };

  if (loading || error || isEmpty(initialValues) || isEmpty(disabled))
    return <Loading />;

  return (
    <FieldRegistryProvider store={fieldStore}>
      <View
        active={active}
        setActive={setActive}
        data={omitDeep(data, "__typename")}
        progress={progress}
        setProgress={setProgress}
        disabled={disabled}
        setDisabled={setDisabled}
        setPercent={calculateRealTimeProgress}
        realProgress={realProgress}
        calculateProgressMean={calculateProgressMean}
        initialValues={initialValues}
        checkUser={checkUser}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </FieldRegistryProvider>
  );
};

export default Detail;
